import { cloneGraphNodes, normalizeGraphNodes } from '../graph/nodeDefs.js';
import type { GraphEdge, GraphNode } from '../graph/nodeDefs.js';
import type { IntrinsicSizeMode } from '../graph/graphRuntime.js';
import { RENDER_NODE_DEFS } from '../graph/renderNodeDefs.js';
import {
  getDefaultGeometryLibraryId,
  getDefaultMaterialLibraryId,
} from '../ids/entityIds.js';

export interface ResolvedGraph {
  nodes: GraphNode[];
  edges: GraphEdge[];
  sizeMode?: IntrinsicSizeMode;
}

export interface LibraryGeometryDef extends ResolvedGraph {
  id: string;
  name: string;
  sizeMode?: IntrinsicSizeMode;
  builtIn: boolean;
}

export interface LibraryMaterialDef extends ResolvedGraph {
  id: string;
  name: string;
  mode: 'scalar' | 'color';
  builtIn: boolean;
}

export type GeometrySlot =
  | { type: 'library'; id: string }
  | { type: 'inline'; nodes: GraphNode[]; edges: GraphEdge[]; sizeMode?: IntrinsicSizeMode };

export type MaterialSlot =
  | { type: 'library'; id: string }
  | { type: 'inline'; nodes: GraphNode[]; edges: GraphEdge[] };

export interface LayerDef {
  id: string;
  name: string;
  geometry: GeometrySlot;
  material: MaterialSlot;
  builtIn?: boolean;
}

export interface GraphLibrary<T extends ResolvedGraph> {
  builtIn: Record<string, T>;
  user: Record<string, T>;
}

export interface LayerGraphLibraries {
  geometryLibrary: GraphLibrary<LibraryGeometryDef>;
  materialLibrary: GraphLibrary<LibraryMaterialDef>;
}

export function cloneGraphEdges(edges: GraphEdge[]): GraphEdge[] {
  return edges.map(edge => ({ ...edge }));
}

export function cloneResolvedGraph(nodes: GraphNode[], edges: GraphEdge[], sizeMode?: IntrinsicSizeMode): ResolvedGraph {
  return {
    nodes: cloneGraphNodes(nodes),
    edges: cloneGraphEdges(edges),
    sizeMode,
  };
}

export function normalizeResolvedGraph(graph: ResolvedGraph): ResolvedGraph {
  const nodes = normalizeGraphNodes(graph.nodes, RENDER_NODE_DEFS);
  return {
    nodes,
    edges: cloneGraphEdges(graph.edges),
    sizeMode: graph.sizeMode,
  };
}

export function normalizeSlot<T extends GeometrySlot | MaterialSlot>(slot: T): T {
  if (slot.type === 'library') return slot;
  const nodes = normalizeGraphNodes(slot.nodes, RENDER_NODE_DEFS);
  return {
    ...slot,
    nodes,
    edges: cloneGraphEdges(slot.edges),
  } as T;
}

export function normalizeLayerDef(layer: LayerDef): LayerDef {
  return {
    ...layer,
    geometry: normalizeSlot(layer.geometry),
    material: normalizeSlot(layer.material),
  };
}

export function normalizeGraphLibrary<T extends ResolvedGraph>(
  library: Record<string, T>,
): Record<string, T> {
  return Object.fromEntries(
    Object.entries(library).map(([id, entry]) => [id, {
      ...entry,
      ...normalizeResolvedGraph(entry),
    }]),
  );
}

export function createDefaultGeometrySlot(): GeometrySlot {
  return { type: 'library', id: getDefaultGeometryLibraryId() };
}

export function createDefaultMaterialSlot(): MaterialSlot {
  return { type: 'library', id: getDefaultMaterialLibraryId() };
}

export function findLayerGraphLibraryItem(
  libraries: LayerGraphLibraries,
  kind: 'geometry' | 'material',
  id: string,
): LibraryGeometryDef | LibraryMaterialDef | undefined {
  const library = kind === 'geometry'
    ? libraries.geometryLibrary
    : libraries.materialLibrary;
  return library.builtIn[id] ?? library.user[id];
}

export function resolveSlot(
  libraries: LayerGraphLibraries,
  slot: GeometrySlot | MaterialSlot,
  kind: 'geometry' | 'material',
): ResolvedGraph | null {
  if (slot.type === 'library') {
    const item = findLayerGraphLibraryItem(libraries, kind, slot.id);
    if (!item) return null;
    return cloneResolvedGraph(
      item.nodes,
      item.edges,
      kind === 'geometry' ? (item as LibraryGeometryDef).sizeMode : undefined,
    );
  }

  return cloneResolvedGraph(
    slot.nodes,
    slot.edges,
    'sizeMode' in slot ? slot.sizeMode : undefined,
  );
}

export function resolveGeometrySlot(libraries: LayerGraphLibraries, slot: GeometrySlot): ResolvedGraph | null {
  return resolveSlot(libraries, slot, 'geometry');
}

export function resolveMaterialSlot(libraries: LayerGraphLibraries, slot: MaterialSlot): ResolvedGraph | null {
  return resolveSlot(libraries, slot, 'material');
}
